import { Injectable, OnDestroy } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { PlatformServiceCommand } from "@fabric-types/svc-platform";
import { GeolocationShowGeofencingErrorAlertPayload } from "@fabric-types/svc-platform/models/geolocation.types";
import { Store } from "@ngrx/store";
import { DialogService } from "@obg/mat.ui.core";
import {
	CommonState,
	CountryAreaStatus,
	GeoFencingAction,
	GEOFENCING_ERROR,
	GeoFencingErrorType,
	GeofencingConfigService,
	GeofencingSelector,
} from "@obg/ngx.common";
import {
	AbstractAlertOptions,
	AbstractTranslatableAlertService,
	Jurisdiction,
	JurisdictionSelector,
} from "@obg/ngx.core";
import { DeviceConfigService, DeviceType, TelemetryService } from "@obg/ngx.global";
import { DocumentRef, PlatformService } from "@obg/ngx.global.evo";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, EMPTY } from "rxjs";
import { auditTime, exhaustMap, filter, finalize, switchMap, take, tap } from "rxjs/operators";

import { GeofencingAlertEvent } from "../telemetry/ui-interaction-telemetry.model";

import {
	GEOFENCING_ERROR_ALERT_ID,
	GEOFENCING_TRANSLATION_KEYS,
	GEOLOCATION_PERMISSION_NOTICE_ID,
	GTM_GEOFENCING_ALERT_SUB_AREA,
} from "./geofencing-alert.const";
import { GeofencingAlertPermissionComponent } from "./geofencing-alert.permission.component";
import { GeolocationDialogComponent } from "./geolocation-dialog/geolocation-dialog.component";
import { WageringBlockDialogContainer } from "./wagering-block/wagering-block-dialog.container";

@Injectable({ providedIn: "root" })
export class GeofencingAlertService implements OnDestroy {
	private showPermissionsNotification$$ = this.permissionNotification$().subscribe();

	constructor(
		private alertService: AbstractTranslatableAlertService,
		private store: Store<CommonState>,
		private deviceConfigService: DeviceConfigService,
		private platformService: PlatformService,
		private telemetryService: TelemetryService,
		private dialogService: DialogService,
		private geofencingConfigService: GeofencingConfigService,
		private matDialog: MatDialog,
		private docRef: DocumentRef
	) {
		setTimeout(() => {
			this.docRef.obgEventBus?.listen(
				PlatformServiceCommand.GeolocationShowGeofencingErrorAlert,
				(payload: GeolocationShowGeofencingErrorAlertPayload) => {
					this.showGeofencingErrorAlert$(
						GEOFENCING_ERROR.InvalidCountryAccess,
						payload.jurisdiction as Jurisdiction,
						undefined
					)
						.pipe(take(1))
						// eslint-disable-next-line rxjs/no-ignored-subscription
						.subscribe();
				}
			);
		}, 0);
	}

	ngOnDestroy(): void {
		this.showPermissionsNotification$$.unsubscribe();
	}

	initialize$() {
		return this.geofencingError$();
	}

	permissionNotification$() {
		if (this.platformService.isServer) {
			return EMPTY;
		}

		return this.store.select(GeofencingSelector.showNotification).pipe(
			auditTime(2500),
			filter((showNotification) => showNotification),
			switchMap(() =>
				combineLatest([
					this.showPermissionNotification$(),
					this.store.select(GeofencingSelector.showNotification),
				]).pipe(
					filter(([, showNotification]) => !showNotification),
					take(1),
					tap(([ref]) => ref.close())
				)
			)
		);
	}

	geofencingError$() {
		return this.store.select(GeofencingSelector.isValid).pipe(
			filter((isValid) => isValid === false),
			switchMap(() =>
				combineLatest([
					this.store.select(GeofencingSelector.getError),
					this.store.select(GeofencingSelector.countryAreaStatus),
					this.store.select(GeofencingSelector.isWageringBlockDialogEnabled),
					this.store.select(GeofencingSelector.locationId),
					this.store.select(JurisdictionSelector.get),
				])
			),
			ignoreNil(),
			filter(([error]) => error !== undefined),
			exhaustMap(([error, countryAreaStatus, wageringBlockDialogEnabled, locationId, jurisdiction]) => {
				if (this.isGeofencingErrorDialogOpen()) {
					return EMPTY;
				}

				return countryAreaStatus === CountryAreaStatus.Restricted && wageringBlockDialogEnabled
					? this.showWageringBlockedAlert$()
					: this.showGeofencingErrorAlert$(error, jurisdiction, locationId);
			})
		);
	}

	showWageringBlockedAlert$() {
		return this.alertService
			.open$(
				{
					testId: GEOFENCING_ERROR_ALERT_ID,
					messageComponent: WageringBlockDialogContainer,
					hideClose: false,
					dialogConfig: {
						panelClass: "wagering-block-dialog",
					},
				},
				[]
			)
			.pipe(switchMap((ref) => ref.afterClosed()));
	}

	showPermissionNotification$() {
		return this.alertService.open$(
			{
				id: GEOLOCATION_PERMISSION_NOTICE_ID,
				title: GEOFENCING_TRANSLATION_KEYS.locationAccessTitle,
				message: GEOFENCING_TRANSLATION_KEYS.locationAccessText,
				messageComponent: GeofencingAlertPermissionComponent,
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
			},
			[]
		);
	}

	showGeofencingErrorAlert$(
		error: GeoFencingErrorType | undefined,
		jurisdiction: Jurisdiction | string | undefined,
		locationId: string | undefined
	) {
		const isMobile = this.deviceConfigService.getType() === DeviceType.Mobile;
		const dialogMessageKey = this.getDialogMessageKey(error);

		this.telemetryService.track({
			SubArea: GTM_GEOFENCING_ALERT_SUB_AREA,
			event: GeofencingAlertEvent.GeofencingAlertSeen,
			Reason: error,
		});

		if (
			jurisdiction !== Jurisdiction.Iplyc &&
			jurisdiction !== Jurisdiction.Lotba &&
			jurisdiction !== Jurisdiction.LPCSE &&
			jurisdiction !== Jurisdiction.BRA
		) {
			return this.alertService
				.open$(
					{
						id: GEOFENCING_ERROR_ALERT_ID,
						testId: GEOFENCING_ERROR_ALERT_ID,
						title: GEOFENCING_TRANSLATION_KEYS.errorTitle,
						message: dialogMessageKey,
						redirectButtonText: GEOFENCING_TRANSLATION_KEYS.redirectButtonText,
						hideClose: false,
						customAlertComponent: GeolocationDialogComponent,
					},
					[]
				)
				.pipe(switchMap((ref) => ref.afterClosed()));
		}

		const redirectButtonText = this.getRedirectButtonText(locationId);

		if (isMobile) {
			return this.dialogService
				.openComponent(GeolocationDialogComponent, {
					autoFocus: false,
					id: GEOFENCING_ERROR_ALERT_ID,
					data: this.createAlertOptions(
						GEOFENCING_TRANSLATION_KEYS.errorTitle,
						dialogMessageKey,
						redirectButtonText
					),
					panelClass: "argentine-mobile-geolocation-dialog",
				})
				.afterClosed();
		}

		return this.alertService
			.open$(
				{
					id: GEOFENCING_ERROR_ALERT_ID,
					testId: GEOFENCING_ERROR_ALERT_ID,
					title: GEOFENCING_TRANSLATION_KEYS.errorTitle,
					message: dialogMessageKey,
					redirectButtonText,
					hideClose: false,
					customAlertComponent: GeolocationDialogComponent,
				},
				[]
			)
			.pipe(
				switchMap((ref) =>
					ref.afterClosed().pipe(
						finalize(() => {
							if (jurisdiction === Jurisdiction.BRA) {
								this.store.dispatch(GeoFencingAction.resetError());
							}
						})
					)
				)
			);
	}

	isGeofencingErrorDialogOpen(): boolean {
		return !!this.matDialog.openDialogs.find((dialog) => dialog.id === GEOFENCING_ERROR_ALERT_ID);
	}

	private getDialogMessageKey(error: GeoFencingErrorType | undefined): string {
		switch (error) {
			case GEOFENCING_ERROR.PermissionDenied:
				return GEOFENCING_TRANSLATION_KEYS.disabledText;
			case GEOFENCING_ERROR.InvalidPosition:
				return GEOFENCING_TRANSLATION_KEYS.invalidPosition;
			case GEOFENCING_ERROR.InvalidCountryAccess:
				return GEOFENCING_TRANSLATION_KEYS.invalidCountryAccess;
			case GEOFENCING_ERROR.RadarGeofencingGeneralError:
				return GEOFENCING_TRANSLATION_KEYS.generalError;
			case GEOFENCING_ERROR.RadarGeofencingErrorMultipleTries:
				return GEOFENCING_TRANSLATION_KEYS.multipleTriesError;
			case GEOFENCING_ERROR.RadarGeofencingServerError:
				return GEOFENCING_TRANSLATION_KEYS.serverError;
			default:
				return GEOFENCING_TRANSLATION_KEYS.errorText;
		}
	}

	private getRedirectButtonText(locationId: string | undefined): string {
		const navigateToUrl = this.geofencingConfigService.getArgentinaNavigateToUrl(locationId ?? "");
		const formattedLocationId = locationId?.toLowerCase().replace(/ /g, ".");

		return !navigateToUrl || !formattedLocationId
			? GEOFENCING_TRANSLATION_KEYS.redirectButtonText
			: `${GEOFENCING_TRANSLATION_KEYS.redirectButtonText}-${formattedLocationId}`;
	}

	private createAlertOptions(
		title: string,
		message: string,
		redirectButtonText?: string,
		hideDialogFooter = false
	): AbstractAlertOptions {
		return {
			id: GEOFENCING_ERROR_ALERT_ID,
			testId: GEOFENCING_ERROR_ALERT_ID,
			hideDialogFooter,
			title,
			message,
			redirectButtonText,
			hideClose: false,
		};
	}
}
